import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../store/index";
import {
  getSumConfigurationRequest,
  getSystemConfigRequest,
  getSystemConfigSchemaRequest,
  saveSystemConfigRequest,
  saveSystemConfigSchemaRequest,
  type SystemConfigRequestKey,
} from "../store/systemConfigurationDuck";
import { combineErrors, combineStatuses } from "../utils";

export function useSystemConfig() {
  const dispatch = useDispatch();

  const { schema, currentConfig, currentConfigOperatorName, sumConfiguration } =
    useSelector((state: RootState) => state.systemConfig);

  const loadSumConfiguration = useCallback(() => {
    dispatch(getSumConfigurationRequest());
  }, [dispatch]);

  const loadConfig = useCallback(
    (operatorName: string) => {
      dispatch(getSystemConfigRequest({ operatorName }));
    },
    [dispatch],
  );

  const saveConfig = useCallback(
    (operatorName: string, config: Record<string, unknown>) => {
      dispatch(saveSystemConfigRequest({ operatorName, config }));
    },
    [dispatch],
  );

  const loadSchema = useCallback(
    (operatorName: string) => {
      dispatch(getSystemConfigSchemaRequest({ operatorName }));
    },
    [dispatch],
  );

  const saveSchema = useCallback(
    (operatorName: string, schema: Record<string, unknown>) => {
      dispatch(saveSystemConfigSchemaRequest({ operatorName, schema }));
    },
    [dispatch],
  );

  const getRequestStatus = useCallback(
    (request: SystemConfigRequestKey | SystemConfigRequestKey[]) =>
      useSelector((state: RootState) => {
        const requests = Array.isArray(request) ? request : [request];
        return combineStatuses(
          requests.map((key) => state.systemConfig.requests[key].status),
        );
      }),
    [],
  );

  const getRequestError = useCallback(
    (request: SystemConfigRequestKey | SystemConfigRequestKey[]) =>
      useSelector((state: RootState) => {
        const requests = Array.isArray(request) ? request : [request];

        return combineErrors(
          requests.map((key) => state.systemConfig.requests[key].error),
        );
      }),
    [],
  );

  const getRequestFieldErrors = useCallback(
    (request: SystemConfigRequestKey) =>
      useSelector(
        (state: RootState) => state.systemConfig.requests[request].fieldErrors,
      ),
    [],
  );

  return {
    schema,
    currentConfig,
    currentConfigOperatorName,
    sumConfiguration,
    loadSumConfiguration,
    loadConfig,
    loadSchema,
    saveConfig,
    saveSchema,
    getRequestStatus,
    getRequestError,
    getRequestFieldErrors,
  };
}
